import { stateConvert } from "../utils/stateAbbreviation";

export const sortListOfObjects = (type, list, order, key) => {
  return list.sort((a, b) => {
    // utility function to get the nested keys
    const getValue = (obj, nestedKey) => nestedKey.split('.').reduce((acc, part) => acc && acc[part], obj)
    const valA = getValue(a, key), valB = getValue(b, key)
    if (type === "string") {
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      return order === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
    }
    if (type === "number") {
      const numA = parseFloat(valA);
      const numB = parseFloat(valB);
      return order === "asc" ? numA - numB : numB - numA;
    }
    return 0;
  });
};

const getCanadaStates = [
  { label: "AB-Alberta", isoCode: "AB", value: "Alberta", name: "Alberta" },
  { label: "BC-British Columbia", isoCode: "BC", value: "British Columbia", name: "British Columbia" },
  { label: "MB-Manitoba", isoCode: "MB", value: "Manitoba", name: "Manitoba" },
  { label: "NB-New Brunswick", isoCode: "NB", value: "New Brunswick", name: "New Brunswick" },
  { label: "NL-Newfoundland and Labrador", isoCode: "NL", value: "Newfoundland and Labrador", name: "Newfoundland and Labrador" },
  { label: "NS-Nova Scotia", isoCode: "NS", value: "Nova Scotia", name: "Nova Scotia" },
  { label: "ON-Ontario", isoCode: "ON", value: "Ontario", name: "Ontario" },
  { label: "PE-Prince Edward Island", isoCode: "PE", value: "Prince Edward Island", name: "Prince Edward Island" },
  { label: "QC-Quebec", isoCode: "QC", value: "Quebec", name: "Quebec" },
  { label: "SK-Saskatchewan", isoCode: "SK", value: "Saskatchewan", name: "Saskatchewan" },
  { label: "NT-Northwest Territories", isoCode: "NT", value: "Northwest Territories", name: "Northwest Territories" },
  { label: "NU-Nunavut", isoCode: "NU", value: "Nunavut", name: "Nunavut" },
  { label: "YT-Yukon", isoCode: "YT", value: "Yukon", name: "Yukon" }
];

const normalizeCanadaStateName = (value) => {
  if (!value) return value;

  const normalizedValue = value.trim().toLowerCase();
  const aliasMap = {
    "yukon territory": "yukon",
    "the yukon": "yukon",
  };

  return aliasMap[normalizedValue] || normalizedValue;
};

const getCanadaStateAbbrByName = (value) => {
  const normalizedValue = normalizeCanadaStateName(value);
  const state = getCanadaStates.find((item) => item.name.toLowerCase() === normalizedValue);
  return state ? state.isoCode : value;
};

const customSelectStylesForLocale = {
  control: (provided) => ({
    ...provided,
    minHeight: '28px',
    height: '28px',
    width: '95px',
    fontSize: '12px',
    borderRadius: '4px',
    borderColor: '#ccc',
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#999',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '28px',
    padding: '0 6px',
  }),
  singleValue: (provided) => ({
    ...provided,
    lineHeight: '28px',
  }),
  option: (provided) => ({
    ...provided,
    fontSize: '12px',
    padding: '4px 8px',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '4px',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: '4px',
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: '12px',
  }),
};

const getCurrencyCodeByCountry = (country = "USA") => {
  switch ((country || "").toUpperCase()) {
    case "CANADA":
      return "CAD";
    default:
      return "USD";
  }
};

const getLoggedInUserCountry = () => {
  const loggedInUserInfo = JSON.parse(localStorage.getItem("loggedInUserData"));
  return loggedInUserInfo?.country || "USA";
};

const formatCurrencyByCountry = (value, country = "USA", fixed = 2) => {
  if (value === null || value === undefined || value === "") return "";

  const formatSingle = (val) => {
    const num = Number(val);
    if (Number.isNaN(num)) return value;

    const formatted = num.toLocaleString("en-US", {
      minimumFractionDigits: fixed,
      maximumFractionDigits: fixed,
    });
    if (country && country.toUpperCase() === "CANADA") {
      return `$${formatted} CAD`;
    }
    // Default: USA
    return `$ ${formatted}`;
  };

  // If value is an array → format each and join as string
  if (Array.isArray(value)) {
    return value
      .map(formatSingle)
      .filter(Boolean) // remove invalid entries
      .join(", ");
  }

  // Single value
  return formatSingle(value);
};

const getStateAbbr = (state, country = "USA") => {
  const normalizedCountry = country?.trim().toUpperCase();
  const stateAbbr = normalizedCountry === "CANADA"
    ? getCanadaStateAbbrByName(state)
    : stateConvert(state) || state;

  return stateAbbr || "";
};

const equipmentMapping = {
  FLAT_BED: 'Flatbed',
  ENCLOSED_TRAILER: 'Enclosed Trailer',
  STRAIGHT_TRUCK: 'Straight Truck',
};

const formatEquipmentRequirements = (equip, defaultValue = 'NA') => {
  if (!equip) return defaultValue;

  let data = equip;
  if (typeof equip === 'object' && !Array.isArray(equip)) {
    data = equip.orderDetails?.equipmentRequirements || equip.equipmentRequirements;
    if (!data) return defaultValue;
  }

  const formatItem = (item) => {
    if (!item || typeof item !== 'string') return item || '';
    const trimmed = item.trim();
    return equipmentMapping[trimmed] || equipmentMapping[trimmed.toUpperCase()] || trimmed;
  };

  if (Array.isArray(data)) {
    const items = [];
    data.forEach((item) => {
      if (typeof item === 'string' && item.includes(',')) {
        items.push(...item.split(','));
      } else {
        items.push(item);
      }
    });
    const formatted = items.map(formatItem).filter(Boolean);
    return formatted.length > 0 ? formatted.join(', ') : defaultValue;
  }

  if (typeof data === 'string') {
    const items = data.split(',').map(formatItem).filter(Boolean);
    return items.length > 0 ? items.join(', ') : defaultValue;
  }

  return defaultValue;
};

export {
  getCanadaStateAbbrByName,
  getCanadaStates,
  customSelectStylesForLocale,
  formatCurrencyByCountry,
  getStateAbbr,
  getCurrencyCodeByCountry,
  getLoggedInUserCountry,
  equipmentMapping,
  formatEquipmentRequirements,
};

